import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import MainLayout from '../../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../../utils';
import formatNumber, { defaultFilterDates, formatSummaryQuantityLabel } from '../../../utils/functions';

const PER_PAGE = 10;

const emptyStats = {
  lines_count: 0,
  total_stock_value: 0,
  total_quantity_in_stock_xof: 0,
  total_quantity_validated_xof: 0,
};

const qtyLabel = (quantity, row) => formatSummaryQuantityLabel(quantity, row);

export default function AdminSummary() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(emptyStats);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const { dateFrom, dateTo } = defaultFilterDates();
    return { search: '', fromDate: dateFrom, toDate: dateTo };
  });
  const [draftFilters, setDraftFilters] = useState(() => {
    const { dateFrom, dateTo } = defaultFilterDates();
    return { fromDate: dateFrom, toDate: dateTo };
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [fromRow, setFromRow] = useState(0);
  const [toRow, setToRow] = useState(0);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/products-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          per_page: PER_PAGE,
          search: appliedFilters.search || undefined,
          fromDate: appliedFilters.fromDate || undefined,
          toDate: appliedFilters.toDate || undefined,
        },
      });

      const paginator = response.data?.data;
      setItems(Array.isArray(paginator?.data) ? paginator.data : []);
      setStats(response.data?.stats ?? emptyStats);
      setLastPage(paginator?.last_page ?? 1);
      setTotalRows(paginator?.total ?? 0);
      setFromRow(paginator?.from ?? 0);
      setToRow(paginator?.to ?? 0);
    } catch (error) {
      console.error('Erreur lors de la récupération du résumé produits :', error);
      setItems([]);
      setStats(emptyStats);
      setLastPage(1);
      setTotalRows(0);
      setFromRow(0);
      setToRow(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, appliedFilters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: draftFilters.fromDate,
      toDate: draftFilters.toDate,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    const { dateFrom, dateTo } = defaultFilterDates();
    setDraftFilters({ fromDate: dateFrom, toDate: dateTo });
    setAppliedFilters({ search: '', fromDate: dateFrom, toDate: dateTo });
    setCurrentPage(1);
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchData();
  };

  const fetchAllForExport = async () => {
    const token = localStorage.getItem('token');
    const count = stats.lines_count || totalRows || 100;
    const response = await axios.get(`${APP_API_URL}/products-summary`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        page: 1,
        per_page: count,
        for_export: true,
        search: appliedFilters.search || undefined,
        fromDate: appliedFilters.fromDate || undefined,
        toDate: appliedFilters.toDate || undefined,
      },
    });
    const paginator = response.data?.data;
    return Array.isArray(paginator?.data) ? paginator.data : items;
  };

  const buildExportRows = (rows) =>
    rows.map((item) => {
      const quantityInStock = parseFloat(item.quantity_in_stock || 0);
      const quantityValidated = parseFloat(item.quantity_validated || 0);
      const quantityReal = quantityInStock + quantityValidated;
      return { item, quantityInStock, quantityValidated, quantityReal };
    });

  const exportToPDF = async () => {
    const rows = buildExportRows(await fetchAllForExport());
    const doc = new jsPDF();
    doc.text('Résumé des produits', 14, 16);
    if (appliedFilters.fromDate || appliedFilters.toDate) {
      doc.setFontSize(9);
      doc.text(
        `Période validée : ${appliedFilters.fromDate || '…'} → ${appliedFilters.toDate || '…'}`,
        14,
        22
      );
    }
    doc.autoTable({
      head: [['Référence', 'Avant validation', 'Validée', 'Après validation']],
      body: rows.map(({ item, quantityInStock, quantityValidated, quantityReal }) => [
        item.product_name,
        qtyLabel(quantityReal, item),
        qtyLabel(quantityValidated, item),
        qtyLabel(quantityInStock, item),
      ]),
      startY: appliedFilters.fromDate || appliedFilters.toDate ? 28 : 24,
      theme: 'striped',
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 9 },
    });
    doc.save(`Produits ${APP_NAME}.pdf`);
  };

  const exportToExcel = async () => {
    const rows = buildExportRows(await fetchAllForExport());
    const worksheetData = rows.map(({ item, quantityInStock, quantityValidated, quantityReal }) => ({
      Référence: item.product_name,
      'Quantité avant validation': qtyLabel(quantityReal, item),
      'Quantité validée': qtyLabel(quantityValidated, item),
      'Quantité après validation': qtyLabel(quantityInStock, item),
    }));
    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produits');
    XLSX.writeFile(wb, `Produits ${APP_NAME}.xlsx`);
  };

  const handlePrint = async () => {
    const rows = buildExportRows(await fetchAllForExport());
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = rows
      .map(
        ({ item, quantityInStock, quantityValidated, quantityReal }) => `
      <tr>
        <td>${item.product_name}</td>
        <td>${qtyLabel(quantityReal, item)}</td>
        <td>${qtyLabel(quantityValidated, item)}</td>
        <td>${qtyLabel(quantityInStock, item)}</td>
      </tr>`
      )
      .join('');

    printWindow.document.write(`
      <html><head><title>Produits</title>
      <style>body{font-family:Arial,sans-serif}table{width:100%;border-collapse:collapse}
      table,th,td{border:1px solid #ccc}th,td{padding:8px;text-align:left}</style></head>
      <body><h1>Résumé des produits</h1>
      ${appliedFilters.fromDate || appliedFilters.toDate ? `<p>Période : ${appliedFilters.fromDate || '…'} → ${appliedFilters.toDate || '…'}</p>` : ''}
      <table>
      <thead><tr><th>Référence</th><th>Avant validation</th><th>Validée</th><th>Après validation</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Administration</p>
          <h1 className="duo-hero-title">Résumé des produits</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Quantités validées filtrées par période (bons de commande approuvés).
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Variantes (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.lines_count}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Valeur magasin (PRU)</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(stats.total_stock_value ?? stats.total_quantity_in_stock_xof)} XOF
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Coût validé sur période (PRU)</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(stats.total_quantity_validated_xof)}
            </p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Détail par produit
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher par référence ou SKU..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <p className="mb-2 small text-muted">
                  Plage de dates sur les <strong>bons de commande validés</strong> (quantité validée).
                </p>
                <div className="duo-filters__grid">
                  <div>
                    <label className="duo-form-label">Date de début</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.fromDate}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de fin</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.toDate}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          {(appliedFilters.fromDate || appliedFilters.toDate) && (
            <div className="px-3 py-2 border-bottom small text-muted" style={{ borderColor: '#f0f0f0' }}>
              Période active : {appliedFilters.fromDate || '…'} → {appliedFilters.toDate || '…'}
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Avant validation</th>
                  <th>Validée</th>
                  <th>Après validation</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item) => {
                    const quantityInStock = parseFloat(item.quantity_in_stock || 0);
                    const quantityValidated = parseFloat(item.quantity_validated || 0);
                    const quantityReal = quantityInStock + quantityValidated;
                    const costPrice = parseFloat(
                      item.prix_unitaire_revient ?? item.variant_price ?? 0
                    );

                    return (
                      <tr key={item.variant_id ?? item.sku}>
                        <td className="fw-bold">{item.product_name}</td>
                        <td>
                          <p className="mb-0 fw-semibold" style={{ fontSize: '0.85rem' }}>
                            {formatSummaryQuantityLabel(quantityReal, item)}
                          </p>
                          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                            {formatNumber(quantityReal * costPrice)} XOF
                          </span>
                        </td>
                        <td>
                          <p className="mb-0 fw-semibold" style={{ fontSize: '0.85rem' }}>
                            {formatSummaryQuantityLabel(quantityValidated, item)}
                          </p>
                          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                            {formatNumber(quantityValidated * costPrice)} XOF
                          </span>
                        </td>
                        <td>
                          <p className="mb-0 fw-semibold" style={{ fontSize: '0.85rem' }}>
                            {formatSummaryQuantityLabel(quantityInStock, item)}
                          </p>
                          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                            {formatNumber(quantityInStock * costPrice)} XOF
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="duo-empty-row">
                      Aucun produit correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalRows > 0 && (
            <div className="duo-footer-bar">
              <span>
                {fromRow} – {toRow} sur {totalRows} entrée{totalRows > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {lastPage}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= lastPage}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
